/**
 * OPBridge config persistence — v2 (pair-once-forever).
 *
 * Multi-bridge from day one: storage shape is `BridgeConfig[]`, never a
 * singleton. The user's gig setup runs OPBridge on at least two machines
 * (dev PC + Mac) and the PWA needs to talk to all of them. The list shape
 * also covers the single-bridge case without special casing.
 *
 * Tokens are LAN-scoped Bearer secrets. localStorage is XSS-readable; the
 * threat model is "user's own iPad on user's own LAN" — acceptable for
 * v1. Stricter storage (IndexedDB with origin-bound encryption, OS
 * keychain) becomes relevant if this PWA ever serves multiple users or
 * runs on untrusted devices.
 *
 * v2 (2026-07-10, DEC-059) adds `bridge_instance_name` as the stable
 * cross-session key (survives IP changes; matches OPBridge's mDNS TXT
 * `instance` field + `/health.instance_name`), plus `scope_bundle_id`,
 * `scope_bundle_version`, and `added_at_us` to support pair-once-forever
 * bundle-based grants + reconnect resync. Legacy field names (`baseUrl`,
 * `name`, `nodeId`, `everConnected`, `defaultMidiOutput`) preserved to
 * keep dispatcher + connection manager blast radius bounded; renames land
 * in Phase B.
 */
// ⚠ PAIR-ONCE-FOREVER CONTRACT: bumping this key (v2 → v3 → …) MUST ship a
// forward-migrator here that carries `bearer` + `bridge_instance_name` +
// `is_primary` across, exactly like migrateV1toV2 does. Without it, every
// end user's stored pairing is silently dropped on the PWA update that ships
// the bump, and they are force-re-paired at the worst possible time. This is
// guarded by two smokes: `pairing_bearer_survives_update` (asserts the booted
// app is still PAIRED, not just that the blob exists) and
// `connect_page_storage_v1_to_v2_migration` (asserts the migrated blob shape).
const STORAGE_KEY = 'opbuttons.opbridges.v2';
const LEGACY_KEY_V1 = 'opbuttons.opbridges.v1';

// ── Stable per-device identity (DEC-468, OPBridge S152) ──────────────────────
// Sent as `device_id` in the pair request so OPBridge can END its pairing-token
// bloat: a re-pair FROM THIS DEVICE replaces this device's own prior token in
// place instead of stacking yet another (OPBridge can't otherwise tell two
// tokens came from the same physical device, so it never dared auto-delete).
// Generated once, persisted; a localStorage clear legitimately reads as "a new
// device" → new id (correct). MUST NOT be shared across installs/devices — two
// devices sharing an id would let one's re-pair revoke the other's token.
// Absent on the wire = today's behaviour (fully backward-compatible), so this
// can never break pairing and never triggers a re-pair.
const DEVICE_ID_KEY = 'opbuttons.device_id.v1';
let cachedDeviceId: string | null = null;

/** The stable id for this browser/install. Cached in-module + persisted in
 *  localStorage; stable across reloads and re-pairs. */
export function deviceId(): string {
  if (cachedDeviceId !== null) return cachedDeviceId;
  if (typeof localStorage === 'undefined') {
    // No persistence (SSR / test harness): a per-session id is still valid — it
    // just won't survive a reload, which those environments don't need.
    cachedDeviceId = freshUuid();
    return cachedDeviceId;
  }
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = freshUuid();
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  cachedDeviceId = id;
  return id;
}

/** High-entropy UUIDv4. Prefers `crypto.randomUUID`; falls back to a
 *  `getRandomValues`-built v4 for engines without it (older Safari). */
function freshUuid(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const b = new Uint8Array(16);
  c.getRandomValues(b);
  b[6] = (b[6]! & 0x0f) | 0x40; // version 4
  b[8] = (b[8]! & 0x3f) | 0x80; // variant 10xx
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0'));
  return `${h[0]}${h[1]}${h[2]}${h[3]}-${h[4]}${h[5]}-${h[6]}${h[7]}-${h[8]}${h[9]}-${h[10]}${h[11]}${h[12]}${h[13]}${h[14]}${h[15]}`;
}

/** The default MIDI route a freshly-paired bridge inherits.
 *
 *  `OPBridge-Ableton` since 2026-09-01 (operator instruction), replacing
 *  `OPButtons`. That is the port the gig rig actually routes through: Live's
 *  own log on the Mac reads `OPBridge-Ableton [Track=true, Remote=true]`
 *  while `OPButtons` is present but `Track=false, Remote=false` — enabled for
 *  nothing, so anything sent there lands nowhere. It also matches OPBridge's
 *  `OPBridge-<purpose>` naming scheme, so it is the direction of travel
 *  rather than a one-off.
 *
 *  ⚠️ THIS NAME IS NOT UNIVERSAL ACROSS THE MESH, measured the same day off
 *  live `GET /midi/ports` on both machines: the Windows dev PC has NO
 *  `OPBridge-Ableton` port at all (it has `OPButtons` plus OPBridge's own
 *  `OPB-int-Konnex`). A default cannot be right everywhere while the daemon
 *  names its ports differently per platform — that inconsistency is
 *  OPBridge's open ASK 3 (logical vs physical destination naming).
 *
 *  So this is a FALLBACK, not a claim. The per-bridge answer is
 *  `defaultMidiOutput`, which every consumer already reads (dispatch,
 *  ruleSync, timeline/bridgeSync, panic, pre-flight) and
 *  which is settable per row in Settings → Connect. A row pointed at a port
 *  its host lacks says so loudly there rather than failing silently.
 *
 *  Changing this does NOT re-point existing rigs: `hydrateDefaults` and
 *  `migrateV1toV2` materialise the constant into every row on load and
 *  `saveBridges` writes it back, so an already-paired bridge carries its own
 *  literal string. Only fresh pairings inherit a new value. */
export const DEFAULT_MIDI_PORT = 'OPBridge-Ableton';

/** A single OPBridge instance the PWA knows about. `id` is a stable
 *  client-side identifier (we generate it; OPBridge's `node_id` from
 *  /health populates `nodeId` once we can reach the bridge). `paired`
 *  flips true once a Bearer token is present. */
export interface BridgeConfig {
  id: string;
  /** Human label the user types ("Dev PC", "Mac Studio", "StreamPC"). */
  name: string;
  /** Base URL, no trailing slash. Shape depends on how THIS device reached
   *  OPBridge, not on the device type:
   *  - `http://<lan-ip>:<port>` — the classic case, a device on the same LAN
   *    scanning/paired via broadcast discovery. Loopback (127.0.0.1) doesn't
   *    reach a peer device this way — still true for that path.
   *  - `http://127.0.0.1:<port+2>` or `https://opbridge.local:<port+3>` — the
   *    OPButtons-hosted-on-a-suite-subdomain path (DEC-166): loopback IS
   *    reachable here (same-machine browser + OPBridge), and `opbridge.local`
   *    is how a DIFFERENT device on the LAN reaches it once served from a
   *    public origin (mixed-content blocks a plain `http://<lan-ip>` fetch
   *    from an `https://` page — see `discovery.ts` `suiteHostedCandidates`). */
  baseUrl: string;
  /** Bearer token. Null means discovered-but-unpaired. */
  bearer: string | null;
  /** OPBridge's persistent node identifier from /health. Null until we've
   *  successfully reached the bridge at least once. */
  nodeId: string | null;
  /** True the first time /health returned OK. Lets the UI distinguish
   *  "never reached" from "was reachable, now unreachable" (transient
   *  outage during your OPBridge rebuild cycle). */
  everConnected: boolean;
  /** OPBridge requires a port name on every `POST /midi/send`. The PWA
   *  doesn't carry per-action port routing yet (the multi-bridge plan
   *  treats port resolution as a later phase), so each bridge holds a
   *  single default output port. `fireAction()` sends every MIDI step to
   *  this port. Null = unconfigured; the dispatcher refuses to send. */
  defaultMidiOutput: string | null;

  // ─── v2 fields (DEC-059, pair-once-forever) ─────────────────────

  /** Stable per-instance identity from OPBridge's `/health.instance_name`
   *  (e.g. `local-GAMING-PC-sdegNg`). Survives IP changes across LAN
   *  sessions (DHCP, hotspot swap). Empty string on rows created before
   *  the first /health probe completes; populated on connection manager's
   *  first successful poll. */
  bridge_instance_name: string;
  /** Bundle id granted by the pair approval (e.g. `"opbuttons-full"`).
   *  Null on legacy rows (pre-v2) + on rows added via manual URL entry
   *  before pairing completes. */
  scope_bundle_id: string | null;
  /** Bundle version at mint time. OPBridge's WS `bundle_updated` events
   *  (Phase B) will compare against this to detect auto-inherit expansions. */
  scope_bundle_version: number | null;
  /** Microsecond timestamp of pair-approval or manual-add. Used as the
   *  `?since=` cursor on `GET /pairing/inheritances` for reconnect resync
   *  (Phase B). */
  added_at_us: number;
  /** DEC-063: exactly one row is the primary at any time. Primary is
   *  the OPBridge OPButtons actively talks to for MIDI dispatch, engine
   *  state, WS subscriptions. Every other row is a peer of the primary.
   *  Migrator auto-elects the first row as primary on first load when no
   *  row has `is_primary: true`.
   *  [ADAPTED 2026-10-11, DEC-269] "OPButtons never opens a direct
   *  connection to a peer" no longer holds and had already lapsed: per-item
   *  affinity sends to a peer (DEC-169), the timeline state poll reads a
   *  timeline's home peer (DEC-224), the Traffic Monitor opens peer sockets,
   *  and `peerPwaSockets.ts` listens for `pwa_action` on every peer. The
   *  primary's consumer socket still owns every other subscription. */
  is_primary: boolean;

  // ─── DEC-526 opportunistic upgrade (OPBridge suite-cert) ────────────

  /** Cached from `/health.suite_hostname` on a successful poll — the
   *  real, publicly-trusted `<hex>.bridge.overpoweredapps.net` hostname
   *  for this bridge, if it has one. `null` until learned. NEVER used to
   *  overwrite `baseUrl` — purely a hint the connection manager probes
   *  opportunistically on (re)connect, falling straight back to `baseUrl`
   *  on any failure (offline gig, DNS unreachable, etc.). Keeping
   *  `baseUrl` as the untouched, always-working fallback is deliberate:
   *  see the OPButtons/OPBridge notepad exchange 2026-08-13 for why
   *  auto-rewriting `baseUrl` itself was rejected as too risky for a
   *  gig-critical connection path. */
  suite_hostname: string | null;
  /** Cached from `/health.browser_tls_port` alongside `suite_hostname` —
   *  the port to pair it with. Never hardcode `+3`; OPBridge documents
   *  this as operator-overridable. */
  browser_tls_port: number | null;
  /** Cached from `/health.suite_cert_expires_at_unix_s` — when the
   *  publicly-trusted suite cert stops being accepted by browsers.
   *
   *  Reported by the daemon EVEN WHILE `suite_hostname` is withheld; that
   *  asymmetry is deliberate on their side and is the whole instrument here
   *  (`opbridge-core/src/suite_cert.rs::health_fields`, guarded by their test
   *  `expiry_survives_the_renewal_window_but_hostname_does_not`). Diagnostic
   *  only — nothing on the connection path reads it. */
  suite_cert_expires_at_unix_s: number | null;
  /** TRUE when the LAST successful poll reported an expiry but NO hostname —
   *  i.e. the daemon has put this cert inside its own renewal window and is
   *  deliberately no longer advertising the name as safe to connect to.
   *
   *  Read from the daemon's gate rather than re-derived from the expiry,
   *  ON PURPOSE. Their window is a constant in their source
   *  (`RENEWAL_WINDOW_DAYS = 30`, DEC-526); recomputing it here would be a
   *  second copy that silently disagrees the day they change it. Unlike the
   *  fields above this is per-poll truth and DOES clear, because a stale
   *  "renewing" is exactly the wrong thing to keep showing. */
  suite_cert_renewing: boolean;

  // ─── The offline path (WebTransport, 2026-08-31) ────────────────────

  /** Cached from `/health.webtransport` + `/health.ip` on a successful poll:
   *  `https://<lan-ip>:<port>`, the daemon's WebTransport front door.
   *
   *  CACHING THIS IS THE WHOLE POINT, not an optimisation. At a venue with no
   *  internet the suite hostname stops resolving ~2 min in, so `/health`
   *  cannot be fetched to LEARN the offline path — it has to already be on
   *  disk from the last time the rig was online. A bridge that has never been
   *  reached while online therefore has no offline path, which is correct and
   *  unavoidable.
   *
   *  Never written to `baseUrl` — same separation as `suite_hostname`, for
   *  the same reason: `baseUrl` stays the untouched fallback. */
  wt_url: string | null;
  /** SHA-256 pin (64 hex) for the self-signed cert at `wt_url`. ROTATES —
   *  validity is <= 14 days by spec, so this is refreshed from every
   *  successful `/health` and a stale one simply fails the handshake and
   *  falls back to fetch. */
  wt_cert_sha256: string | null;
  /** Unix seconds the CURRENT pinned cert expires. Checked before dialling so
   *  an expired pin costs no handshake attempt at all. Superseded by
   *  `wt_hashes_valid_until_unix_s` once the daemon publishes a chain. */
  wt_expires_at_unix_s: number | null;
  /** EVERY pin known for this bridge - the current one plus any pre-minted
   *  future certs the daemon published. This is what makes a long offline gap
   *  survivable.
   *
   *  The problem it solves, precisely: a single pin dies around day 10, not
   *  day 13, because the daemon re-mints 3 days early and starts presenting
   *  the NEXT cert. A device that had not connected since then arrives at a
   *  venue holding a hash for a cert nobody serves - and with no internet it
   *  cannot learn the new one. Holding the chain removes the cliff.
   *
   *  Null on every daemon that publishes no chain (all of them today), in
   *  which case the single `wt_cert_sha256` is used and nothing changes. */
  wt_cert_hashes: string[] | null;
  /** Unix seconds the LAST pin in `wt_cert_hashes` expires - i.e. the real
   *  offline horizon. Null when no chain is published. */
  wt_hashes_valid_until_unix_s: number | null;
}

export function loadBridges(): BridgeConfig[] {
  if (typeof localStorage === 'undefined') return [];
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      const hydrated = parsed.filter(isBridgeConfig).map(hydrateDefaults);
      // Enforce DEC-063 primary invariant on load. If storage was
      // written by pre-DEC-063 code, no row has `is_primary: true` —
      // elect the first. Persist so subsequent loads skip the fixup.
      const elected = electPrimary(hydrated);
      const needsResave = hydrated.some((r, i) => r.is_primary !== elected[i]!.is_primary);
      if (needsResave) saveBridges(elected);
      return elected;
    } catch {
      return [];
    }
  }
  // No v2 store — check if a v1 store exists to migrate forward.
  const legacy = localStorage.getItem(LEGACY_KEY_V1);
  if (!legacy) return [];
  try {
    const parsed = JSON.parse(legacy);
    if (!Array.isArray(parsed)) return [];
    const migrated = electPrimary(
      parsed.filter(isLegacyV1BridgeConfig).map(migrateV1toV2),
    );
    // Persist migration atomically so subsequent boots skip the branch.
    saveBridges(migrated);
    localStorage.removeItem(LEGACY_KEY_V1);
    return migrated;
  } catch {
    return [];
  }
}

export function saveBridges(bridges: BridgeConfig[]): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(bridges));
}

/** Subscribe to bridge-storage writes made by a DIFFERENT tab/window of this
 *  same browser (2026-08-13). The native `storage` event only ever fires in
 *  tabs other than the one that wrote, which is exactly the reactive half
 *  that complements `saveBridgesMergingMetadata()`'s merge-on-save half:
 *  that function stops a stale tab from ERASING another tab's addition;
 *  this lets a stale tab actually SEE the addition live, without requiring
 *  a manual reload to notice a mesh peer another tab just paired. No-ops
 *  outside a browser context (SSR / test harness). Returns an unsubscribe
 *  function. */
export function onBridgesStorageChanged(cb: (bridges: BridgeConfig[]) => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const handler = (e: StorageEvent) => {
    if (e.key !== STORAGE_KEY || e.newValue === null) return;
    try {
      const parsed = JSON.parse(e.newValue);
      if (!Array.isArray(parsed)) return;
      cb(electPrimary(parsed.filter(isBridgeConfig).map(hydrateDefaults)));
    } catch { /* malformed cross-tab write — ignore, this tab's own state is untouched */ }
  };
  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
}

/** Merge-on-save variant for metadata-only patches (2026-08-13). Reads
 *  whatever is CURRENTLY in storage before writing, so a routine write from
 *  this tab can never silently erase a bridge a DIFFERENT tab of the same
 *  browser added in the meantime.
 *
 *  Why this exists and `saveBridges()` (raw overwrite) doesn't get the same
 *  treatment: `reportBridgeIdentity()` in store.ts fires on every successful
 *  health poll — every few seconds, for every known bridge — and its own doc
 *  comment already calls it "a metadata update, not a config edit." Before
 *  this, it still did a blind full-list overwrite of `patched`, which is
 *  THIS tab's own view only. Found live (operator's real multi-tab session,
 *  2026-08-13): Tab A auto-adopted a mesh peer via the inheritance-drainer
 *  (2 bridges paired); a few seconds later Tab B — an older tab that never
 *  saw the peer — had its own primary's routine health tick fire
 *  `reportBridgeIdentity`, which blindly saved Tab B's 1-bridge view over
 *  Tab A's 2-bridge write. The peer vanished with no Disconnect click, and a
 *  subsequent "change primary" attempt then treated it as a brand-new
 *  unpaired candidate (a fresh pairing request nobody was there to approve)
 *  instead of a known peer needing no re-pair.
 *
 *  Union by `id`: `patched` rows win for ids they carry (the freshest known
 *  state for THIS tab's own bridges — a metadata patch should still apply).
 *  Anything present only in the current storage (added by another tab) is
 *  preserved as-is, UNLESS its `nodeId` is on the declined-peers list — an
 *  explicit Disconnect from another tab must still stick, exactly like
 *  inheritanceDrainer.ts's own `isPeerDeclined` check. Only ever narrows the
 *  merged set via that decline check, never via simple absence from
 *  `patched` — a metadata patch describes what THIS tab knows changed, not
 *  the complete set of bridges that should exist. `commit()` (explicit
 *  add/remove/rename) intentionally keeps the raw overwrite — those
 *  represent deliberate, complete intent and must not be second-guessed. */
export function saveBridgesMergingMetadata(patched: BridgeConfig[]): void {
  if (typeof localStorage === 'undefined') return;
  let persisted: BridgeConfig[] = [];
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) persisted = parsed.filter(isBridgeConfig).map(hydrateDefaults);
    } catch { /* corrupt storage — proceed with persisted = [], patched still saves */ }
  }
  const byId = new Map(persisted.map((b) => [b.id, b]));
  for (const b of patched) byId.set(b.id, b);
  const merged = electPrimary(
    Array.from(byId.values()).filter((b) => !b.nodeId || !isPeerDeclined(b.nodeId)),
  );
  localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
}

/** Fill v2 defaults on a partially-typed loaded row. Absent fields
 *  become their neutral values; the loader NEVER returns undefined to
 *  callers. */
function hydrateDefaults(cfg: BridgeConfig): BridgeConfig {
  return {
    ...cfg,
    defaultMidiOutput: cfg.defaultMidiOutput ?? DEFAULT_MIDI_PORT,
    bridge_instance_name: cfg.bridge_instance_name ?? '',
    scope_bundle_id: cfg.scope_bundle_id ?? null,
    scope_bundle_version: cfg.scope_bundle_version ?? null,
    added_at_us: cfg.added_at_us ?? Date.now() * 1000,
    is_primary: cfg.is_primary ?? false,
    suite_hostname: cfg.suite_hostname ?? null,
    browser_tls_port: cfg.browser_tls_port ?? null,
    suite_cert_expires_at_unix_s: cfg.suite_cert_expires_at_unix_s ?? null,
    suite_cert_renewing: cfg.suite_cert_renewing ?? false,
    wt_url: cfg.wt_url ?? null,
    wt_cert_sha256: cfg.wt_cert_sha256 ?? null,
    wt_expires_at_unix_s: cfg.wt_expires_at_unix_s ?? null,
    wt_cert_hashes: cfg.wt_cert_hashes ?? null,
    wt_hashes_valid_until_unix_s: cfg.wt_hashes_valid_until_unix_s ?? null,
  };
}

/** Enforce DEC-063 invariant: exactly one row has `is_primary: true`
 *  when the list is non-empty. If zero rows are primary, elect the
 *  first row (order-of-add default). If multiple rows are primary
 *  (should never happen but robust against corrupted storage), keep
 *  the FIRST primary and demote the rest. Idempotent — repeated calls
 *  yield the same output for the same input. */
export function electPrimary(rows: BridgeConfig[]): BridgeConfig[] {
  if (rows.length === 0) return rows;
  let primaryIndex = rows.findIndex((r) => r.is_primary);
  if (primaryIndex < 0) primaryIndex = 0;
  return rows.map((r, i) => ({
    ...r,
    is_primary: i === primaryIndex,
  }));
}

/** v1 → v2 shape lift. Preserves every legacy field (users' existing
 *  DEV_PC row stays paired against `/midi/send` etc.) and backfills the
 *  new fields with neutral values. Real `bridge_instance_name` gets
 *  populated on the next successful /health probe by store.ts. */
function migrateV1toV2(row: LegacyV1BridgeConfig): BridgeConfig {
  return {
    id: row.id,
    name: row.name,
    baseUrl: row.baseUrl,
    bearer: row.bearer,
    nodeId: row.nodeId,
    everConnected: row.everConnected,
    defaultMidiOutput: row.defaultMidiOutput ?? DEFAULT_MIDI_PORT,
    bridge_instance_name: '',
    scope_bundle_id: null,
    scope_bundle_version: null,
    added_at_us: Date.now() * 1000,
    // is_primary populated by electPrimary() after mapping.
    is_primary: false,
    suite_hostname: null,
    browser_tls_port: null,
    suite_cert_expires_at_unix_s: null,
    suite_cert_renewing: false,
    // Learned on the next successful /health, same as suite_hostname above.
    wt_url: null,
    wt_cert_sha256: null,
    wt_expires_at_unix_s: null,
    wt_cert_hashes: null,
    wt_hashes_valid_until_unix_s: null,
  };
}

interface LegacyV1BridgeConfig {
  id: string;
  name: string;
  baseUrl: string;
  bearer: string | null;
  nodeId: string | null;
  everConnected: boolean;
  defaultMidiOutput?: string | null;
}

function isLegacyV1BridgeConfig(v: unknown): v is LegacyV1BridgeConfig {
  if (!v || typeof v !== 'object') return false;
  const r = v as Record<string, unknown>;
  if (
    typeof r.id !== 'string' ||
    typeof r.name !== 'string' ||
    typeof r.baseUrl !== 'string' ||
    !(r.bearer === null || typeof r.bearer === 'string') ||
    !(r.nodeId === null || typeof r.nodeId === 'string') ||
    typeof r.everConnected !== 'boolean'
  ) return false;
  if (r.defaultMidiOutput !== undefined &&
      r.defaultMidiOutput !== null &&
      typeof r.defaultMidiOutput !== 'string') return false;
  return true;
}

function isBridgeConfig(v: unknown): v is BridgeConfig {
  if (!v || typeof v !== 'object') return false;
  const r = v as Record<string, unknown>;
  if (
    typeof r.id !== 'string' ||
    typeof r.name !== 'string' ||
    typeof r.baseUrl !== 'string' ||
    !(r.bearer === null || typeof r.bearer === 'string') ||
    !(r.nodeId === null || typeof r.nodeId === 'string') ||
    typeof r.everConnected !== 'boolean'
  ) return false;
  if (r.defaultMidiOutput !== undefined &&
      r.defaultMidiOutput !== null &&
      typeof r.defaultMidiOutput !== 'string') return false;
  // v2 fields — tolerated absent (hydrateDefaults fills), but if present
  // must be the right shape.
  if (r.bridge_instance_name !== undefined &&
      typeof r.bridge_instance_name !== 'string') return false;
  if (r.scope_bundle_id !== undefined &&
      r.scope_bundle_id !== null &&
      typeof r.scope_bundle_id !== 'string') return false;
  if (r.scope_bundle_version !== undefined &&
      r.scope_bundle_version !== null &&
      typeof r.scope_bundle_version !== 'number') return false;
  if (r.added_at_us !== undefined &&
      typeof r.added_at_us !== 'number') return false;
  if (r.is_primary !== undefined &&
      typeof r.is_primary !== 'boolean') return false;
  if (r.suite_hostname !== undefined &&
      r.suite_hostname !== null &&
      typeof r.suite_hostname !== 'string') return false;
  if (r.browser_tls_port !== undefined &&
      r.browser_tls_port !== null &&
      typeof r.browser_tls_port !== 'number') return false;
  return true;
}

/** Generate a client-side bridge id. Not cryptographic; just unique. */
export function newBridgeId(): string {
  return `br_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
}

// ─── Declined peers (2026-08-13) ─────────────────────────────────────────
//
// `removeBridge()` (the Connect page's "Disconnect" button) deletes the
// row entirely — but `inheritanceDrainer.ts` reacts to ANY node_id with
// no row as "a bridge I've never seen, adopt it," with no way to tell
// "never seen" apart from "the user just explicitly removed this." On a
// real multi-bridge mesh, the peer that's still connected keeps offering
// the disconnected bridge's inheritance, and the drainer re-adds it —
// disconnect ping-pongs forever between two bridges (found live,
// operator's real 2-bridge network, 2026-08-13).
//
// This is a small persisted denylist of node_ids the drainer must not
// auto-adopt. `removeBridge()` writes to it; `addBridge()` /
// `reportBridgeIdentity()` clear an entry the moment the user (not the
// drainer) reconnects that same node_id — an explicit reconnect always
// wins over a stale decline.

const DECLINED_PEERS_KEY = 'opbuttons.declinedPeers.v1';

function loadDeclinedPeers(): Set<string> {
  if (typeof localStorage === 'undefined') return new Set();
  try {
    const raw = localStorage.getItem(DECLINED_PEERS_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((v): v is string => typeof v === 'string'));
  } catch {
    return new Set();
  }
}

function saveDeclinedPeers(peers: Set<string>): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(DECLINED_PEERS_KEY, JSON.stringify(Array.from(peers)));
}

export function isPeerDeclined(nodeId: string): boolean {
  return loadDeclinedPeers().has(nodeId);
}

export function declinePeer(nodeId: string): void {
  const peers = loadDeclinedPeers();
  if (peers.has(nodeId)) return;
  peers.add(nodeId);
  saveDeclinedPeers(peers);
}

export function undeclinePeer(nodeId: string): void {
  const peers = loadDeclinedPeers();
  if (!peers.has(nodeId)) return;
  peers.delete(nodeId);
  saveDeclinedPeers(peers);
}

/**
 * Clear EVERY decline. Called when the operator explicitly pairs a bridge
 * (`store.ts::addBridge`), because that act is a fresh statement of intent
 * about the whole mesh, not just about the one row.
 *
 * Why this is needed and per-node `undeclinePeer` was not enough (found live
 * 2026-08-28): the operator disconnected BOTH bridges, then re-paired one.
 * The other stayed declined forever, so it never auto-adopted while its row
 * still advertised "pairs automatically" - a permanent, invisible veto left
 * over from an intent the operator had already reversed. Clearing only the
 * node being added cannot fix that, because the stale decline is on a
 * DIFFERENT node than the one being paired.
 *
 * ⚠️ The per-session veto itself stays, and deleting it would be a
 * regression, not a simplification: it exists because auto-adoption and an
 * explicit Disconnect otherwise ping-pong forever on a real 2-bridge mesh
 * (found live 2026-08-13). So "Disconnect" still sticks - until the operator
 * deliberately pairs something again, which is the only signal that the
 * earlier refusal is obsolete.
 */
export function undeclineAllPeers(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(DECLINED_PEERS_KEY);
}
